/**
 * Data Cleanup Tab
 * View all transactions broken down by type and delete rogue entries
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { AlertCircle, Trash2, CheckCircle, DollarSign, RefreshCw, AlertTriangle, Coffee, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { api } from '@/utils/apiClient';

export default function DataReconciliationTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [coffeePassDiagnostic, setCoffeePassDiagnostic] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);

  const fetchCoffeePassDiagnostic = useCallback(async () => {
    setDiagnosticLoading(true);
    try {
      const res = await api.get('/admin/reconciliation/coffee-pass-diagnostic');
      setCoffeePassDiagnostic(res.data);
    } catch (err) {
      console.error('Error fetching coffee pass diagnostic:', err);
    } finally {
      setDiagnosticLoading(false);
    }
  }, []);

  const handleSyncCoffeePasses = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/admin/reconciliation/sync-coffee-passes');
      toast.success(`Synced ${res.data.synced_count} coffee pass sales to transactions!`);
      fetchCoffeePassDiagnostic();
      fetchReport();
    } catch (err) {
      toast.error('Error syncing coffee passes');
    } finally {
      setSyncing(false);
    }
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/transactions/breakdown-report');
      setReport(res.data);
    } catch (err) {
      toast.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
    fetchCoffeePassDiagnostic();
  }, [fetchReport, fetchCoffeePassDiagnostic]);

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm(`Delete transaction ${transactionId}? This cannot be undone.`)) {
      return;
    }

    setDeleting(transactionId);
    try {
      await api.delete(`/admin/transactions/${transactionId}`);
      toast.success('Transaction deleted');
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting transaction');
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteAllNonCoffeePass = async () => {
    if (!window.confirm('Delete ALL non-Coffee Pass transactions? This will remove all test/fake orders. Only Coffee Pass sales will remain.')) {
      return;
    }
    if (!window.confirm('FINAL WARNING: This cannot be undone. Click OK to proceed.')) {
      return;
    }

    setDeleting('all');
    try {
      const res = await api.delete('/admin/transactions/cleanup/non-coffee-pass?confirm=true');
      toast.success(res.data.message);
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting transactions');
    } finally {
      setDeleting(null);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-FJ', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(val || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleDateString('en-FJ', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Coffee Pass Sync Card - CRITICAL */}
      <Card className="border-2 border-amber-500 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Coffee className="w-6 h-6 text-amber-600" />
              <div>
                <CardTitle className="text-xl text-amber-800 dark:text-amber-200">Bula Coffee Pass Sync</CardTitle>
                <CardDescription className="text-amber-700 dark:text-amber-300">
                  Ensure all coffee pass sales are recorded in transactions
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchCoffeePassDiagnostic} disabled={diagnosticLoading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${diagnosticLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                onClick={handleSyncCoffeePasses} 
                disabled={syncing}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {syncing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Coffee className="w-4 h-4 mr-2" />
                    Sync Coffee Passes
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {coffeePassDiagnostic ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border">
                  <p className="text-xs text-muted-foreground">Coffee Club Sales</p>
                  <p className="text-2xl font-bold">{coffeePassDiagnostic.counts.coffee_club_sales}</p>
                </div>
                <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border">
                  <p className="text-xs text-muted-foreground">Coffee Club Passes</p>
                  <p className="text-2xl font-bold">{coffeePassDiagnostic.counts.coffee_club_passes}</p>
                </div>
                <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border">
                  <p className="text-xs text-muted-foreground">Orders w/ Pass</p>
                  <p className="text-2xl font-bold">{coffeePassDiagnostic.counts.orders_with_coffee_pass}</p>
                </div>
                <div className="text-center p-3 bg-green-100 dark:bg-green-900/30 rounded-lg border border-green-300 dark:border-green-700">
                  <p className="text-xs text-green-600 dark:text-green-400">In Transactions</p>
                  <p className="text-2xl font-bold text-green-600">{coffeePassDiagnostic.counts.transactions_coffee_pass}</p>
                </div>
                <div className="text-center p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg border border-blue-300 dark:border-blue-700">
                  <p className="text-xs text-blue-600 dark:text-blue-400">Today ({coffeePassDiagnostic.fiji_date})</p>
                  <p className="text-2xl font-bold text-blue-600">{coffeePassDiagnostic.counts.today_coffee_pass_transactions}</p>
                </div>
              </div>
              
              {/* Sync needed alert */}
              {(coffeePassDiagnostic.counts.coffee_club_sales > coffeePassDiagnostic.counts.transactions_coffee_pass ||
                coffeePassDiagnostic.counts.coffee_club_passes > coffeePassDiagnostic.counts.transactions_coffee_pass) && (
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg border border-red-300 dark:border-red-700 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="text-red-700 dark:text-red-400 font-medium">
                    Some coffee pass sales may be missing from transactions. Click &quot;Sync Coffee Passes&quot; to fix.
                  </span>
                </div>
              )}
              
              {coffeePassDiagnostic.counts.coffee_club_sales === coffeePassDiagnostic.counts.transactions_coffee_pass && (
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg border border-green-300 dark:border-green-700 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 dark:text-green-400 font-medium">
                    All coffee pass sales are synced to transactions.
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-amber-600" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Transaction Breakdown</CardTitle>
              <CardDescription>
                See exactly what&apos;s in your unified transactions database
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {report?.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Total Transactions</p>
                <p className="text-3xl font-bold">{report.summary.total_transactions}</p>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-sm text-green-600 dark:text-green-400">Coffee Pass Sales</p>
                <p className="text-3xl font-bold text-green-600">{report.summary.coffee_pass_count}</p>
                <p className="text-sm text-green-600">${formatCurrency(report.summary.coffee_pass_revenue)}</p>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-600 dark:text-red-400">Other Sales (Test Data)</p>
                <p className="text-3xl font-bold text-red-600">{report.summary.regular_sales_count}</p>
                <p className="text-sm text-red-600">${formatCurrency(report.summary.regular_sales_revenue)}</p>
              </div>
              <div className="text-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-600 dark:text-amber-400">Refunds/Cancels</p>
                <p className="text-3xl font-bold text-amber-600">{report.summary.refund_count + report.summary.cancellation_count}</p>
              </div>
            </div>
          )}

          {/* Discrepancy Alert */}
          {report?.discrepancy?.non_coffee_pass_count > 0 && (
            <div className="mt-4 p-4 bg-red-100 dark:bg-red-900/30 rounded-lg border border-red-300 dark:border-red-700">
              <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">{report.discrepancy.message}</span>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                These should be deleted since the cafe hasn&apos;t opened yet - only Coffee Pass sales are real.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coffee Pass Sales - REAL */}
      <Card className="border-green-200 dark:border-green-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
            <Coffee className="w-5 h-5" />
            Coffee Pass Sales ({report?.coffee_pass_sales?.length || 0})
          </CardTitle>
          <CardDescription>
            These are your REAL sales - Bula Coffee Pass purchases
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-h-64 overflow-y-auto">
            {report?.coffee_pass_sales?.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Customer</th>
                    <th className="text-right p-2">Amount</th>
                    <th className="text-left p-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {report.coffee_pass_sales.map((txn, idx) => (
                    <tr key={txn.transaction_id || idx} className="border-b border-muted">
                      <td className="p-2">{formatDate(txn.datetime)}</td>
                      <td className="p-2">{txn.customer_name || txn.customer_email || 'N/A'}</td>
                      <td className="p-2 text-right font-medium">${formatCurrency(txn.total)}</td>
                      <td className="p-2">{txn.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-muted-foreground text-center py-4">No Coffee Pass sales found</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Non-Coffee Pass Sales - FAKE/TEST - Delete These */}
      {report?.regular_sales?.length > 0 && (
        <Card className="border-red-300 dark:border-red-700 border-2">
          <CardHeader className="bg-red-50 dark:bg-red-900/20">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                  <ShoppingCart className="w-5 h-5" />
                  Non-Coffee Pass Sales ({report.regular_sales.length}) - DELETE THESE
                </CardTitle>
                <CardDescription className="text-red-600 dark:text-red-400">
                  These are test/fake orders that should NOT exist. Delete them to fix your revenue.
                </CardDescription>
              </div>
              <Button 
                variant="destructive"
                onClick={handleDeleteAllNonCoffeePass}
                disabled={deleting === 'all'}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {deleting === 'all' ? 'Deleting...' : 'Delete ALL'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-red-100 dark:bg-red-900/30 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Transaction ID</th>
                    <th className="text-left p-2">Customer</th>
                    <th className="text-right p-2">Amount</th>
                    <th className="text-left p-2">Source</th>
                    <th className="text-center p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {report.regular_sales.map((txn, idx) => (
                    <motion.tr 
                      key={txn.transaction_id || idx} 
                      className="border-b border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0, x: -100 }}
                    >
                      <td className="p-2">{formatDate(txn.datetime)}</td>
                      <td className="p-2 font-mono text-xs">{txn.transaction_id?.slice(0, 15)}...</td>
                      <td className="p-2">{txn.customer_name || txn.customer_email || 'N/A'}</td>
                      <td className="p-2 text-right font-medium">${formatCurrency(txn.total)}</td>
                      <td className="p-2">{txn.source}</td>
                      <td className="p-2 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-100"
                          onClick={() => handleDeleteTransaction(txn.transaction_id)}
                          disabled={deleting === txn.transaction_id}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success State */}
      {report?.discrepancy?.non_coffee_pass_count === 0 && (
        <Card className="border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-green-700 dark:text-green-400">
              <CheckCircle className="w-8 h-8" />
              <div>
                <p className="font-medium text-lg">All Clean!</p>
                <p className="text-sm">Only Coffee Pass sales are in your database. Your royalty report will be accurate.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">What This Page Does:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Shows ALL transactions in your unified database</li>
                <li>Separates <strong className="text-green-600">Coffee Pass sales</strong> (real) from <strong className="text-red-600">other sales</strong> (test data)</li>
                <li>Lets you delete individual transactions or all test data at once</li>
                <li>After cleanup, your 4% Royalty Report will show ONLY real Coffee Pass revenue</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
